import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HardHat, FileText, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US");

export default function GCInvite() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ bid_amount: "", schedule: "", clarifications: "", qualifications: "", value_engineering: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gc-invite/${token}`)
      .then(({ data }) => {
        setData(data);
        if (data.bid) {
          setForm({
            bid_amount: data.bid.bid_amount || "", schedule: data.bid.schedule || "",
            clarifications: data.bid.clarifications || "", qualifications: data.bid.qualifications || "",
            value_engineering: data.bid.value_engineering || "",
          });
          setSubmitted(true);
        }
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/gc-invite/${token}/bid`, { ...form, bid_amount: parseFloat(form.bid_amount) || 0, document_ids: [] });
      toast.success("Bid submitted successfully");
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (notFound) return <Center>This invitation link is invalid or has expired.</Center>;
  if (!data) return <Center>Loading invitation…</Center>;

  const { project, documents, invitation } = data;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-[hsl(var(--blueprint))] text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-2">
          <div className="h-8 w-8 bg-primary flex items-center justify-center"><HardHat size={20} weight="fill" /></div>
          <span className="font-heading font-black text-lg tracking-tighter">CONSTRUCT<span className="text-primary">HUB</span></span>
          <span className="ml-auto overline text-white/60">GC Bid Portal</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="overline text-primary mb-2">Bid Invitation for {invitation.gc_name}</div>
        <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">{project?.name}</h1>
        <p className="text-muted-foreground mt-1">{project?.brand} · {project?.address}</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <Info label="Estimated Budget" value={fmtMoney(project?.estimated_budget)} />
          <Info label="Bid Due Date" value={invitation.bid_due_date || "TBD"} />
          <Info label="Project Number" value={project?.number || "—"} />
        </div>
        {invitation.message && <div className="mt-4 border-l-2 border-primary pl-4 text-sm text-muted-foreground">{invitation.message}</div>}

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          {/* Plans */}
          <div>
            <h2 className="font-heading font-bold text-lg tracking-tight mb-3">Project Plans</h2>
            {documents?.length ? (
              <div className="border border-border bg-card divide-y divide-border">
                {documents.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid={`gc-doc-${d.id}`}>
                    <FileText size={22} weight="duotone" className="text-primary shrink-0" />
                    <div className="min-w-0 flex-1"><div className="font-medium truncate">{d.name}</div><div className="text-xs text-muted-foreground">{d.category} · v{d.current_version}</div></div>
                    <a href={`${API}/gc-invite/${token}/document/${d.id}`} target="_blank" rel="noreferrer" data-testid={`gc-download-${d.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-150"><DownloadSimple size={20} /></a>
                  </div>
                ))}
              </div>
            ) : <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-sm">No plans uploaded yet.</div>}
          </div>

          {/* Bid form */}
          <div>
            <h2 className="font-heading font-bold text-lg tracking-tight mb-3">Submit Your Bid</h2>
            {submitted && (
              <div className="border border-primary bg-primary/5 p-3 mb-4 flex items-center gap-2 text-sm" data-testid="bid-submitted-banner">
                <CheckCircle size={20} weight="fill" className="text-primary" /> Bid submitted. You can update it below.
              </div>
            )}
            <form onSubmit={submit} className="space-y-4 bg-card border border-border p-5" data-testid="gc-bid-form">
              <div className="space-y-2"><Label className="overline">Bid Amount *</Label><Input type="number" value={form.bid_amount} onChange={set("bid_amount")} required data-testid="bid-amount-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Proposed Schedule</Label><Textarea value={form.schedule} onChange={set("schedule")} data-testid="bid-schedule-input" className="rounded-none" rows={2} placeholder="e.g. 14 months, complete by Q3 2027" /></div>
              <div className="space-y-2"><Label className="overline">Clarifications</Label><Textarea value={form.clarifications} onChange={set("clarifications")} data-testid="bid-clarifications-input" className="rounded-none" rows={2} /></div>
              <div className="space-y-2"><Label className="overline">Qualifications</Label><Textarea value={form.qualifications} onChange={set("qualifications")} data-testid="bid-qualifications-input" className="rounded-none" rows={2} /></div>
              <div className="space-y-2"><Label className="overline">Value Engineering Suggestions</Label><Textarea value={form.value_engineering} onChange={set("value_engineering")} data-testid="bid-ve-input" className="rounded-none" rows={2} /></div>
              <Button type="submit" disabled={busy} data-testid="submit-bid-button" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                {busy ? "Submitting…" : submitted ? "Update Bid" : "Submit Bid"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-background"><div className="overline text-muted-foreground">{children}</div></div>
);
const Info = ({ label, value }) => (
  <div className="bg-card border border-border p-4"><div className="overline text-muted-foreground mb-1">{label}</div><div className="font-heading font-bold text-lg tracking-tight">{value}</div></div>
);
